import { zodResolver } from '@hookform/resolvers/zod/dist/zod'
import { EMAIL_IS_INVALID, FIELD_IS_REQUIRED } from 'const'
import moment from 'moment/moment'
import { AccessCode, EAccessCodeStatus, EAccessCodeType, Nullish } from 'types'
import { any, boolean, coerce, literal, object, string, TypeOf, ZodIssueCode } from 'zod'

const organizationValidationText = 'The identifier of organization must contain 2 digits'
const specificUnitIDValidationText =
  'The identifier of specific unit must contain 2 characters (letter and/or digit)'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isExpiryLaterThatEffective = (data: any) => {
  if (!moment(data.expiryDate).isValid() || !moment(data.effectiveDate).isValid()) {
    return true
  }
  return moment(data.expiryDate).isAfter(moment(data.effectiveDate))
}

const accessCodeDraftSchema = object({
  organizationId: string().optional(),
  organizationName: string().min(1, FIELD_IS_REQUIRED),
  organizationType: string().optional(),
  twoDigitsOrg: string().optional(),
  effectiveDate: any().optional(),
  expiryDate: any().optional(),
  state: string().optional(),
  ownerFirstName: string().optional(),
  ownerId: string().optional(),
  ownerLastName: string().optional(),
  ownerEmail: string().email(EMAIL_IS_INVALID).optional().or(literal('')),
  regionalFirstName: string().optional(),
  regionalManagerId: string().optional(),
  regionalLastName: string().optional(),
  regionalEmail: string().email(EMAIL_IS_INVALID).optional().or(literal('')),
  facilityFirstName: string().optional(),
  facilityManagerId: string().optional(),
  facilityLastName: string().optional(),
  facilityEmail: string().email(EMAIL_IS_INVALID).optional().or(literal('')),
  supervisorFirstName: string().optional(),
  supervisorId: string().optional(),
  supervisorLastName: string().optional(),
  supervisorEmail: string().email(EMAIL_IS_INVALID).optional().or(literal('')),
  twoDigitsSpecific: string(),
  specificUnitName: string(),
  duration: string().nullable(),
  numberOfUsers: coerce.number(),
  reason: string(),
})

const commonSchema = {
  code: string().optional(),
  type: string().min(1, FIELD_IS_REQUIRED),
  organizationId: string().optional(),
  supervisorId: string().optional(),
  reason: string(),
  effectiveDate: any().refine(Boolean, FIELD_IS_REQUIRED),
  expiryDate: any().refine(Boolean, FIELD_IS_REQUIRED),
  numberOfUsers: coerce
    .number({
      required_error: FIELD_IS_REQUIRED,
      invalid_type_error: FIELD_IS_REQUIRED,
    })
    .min(1, FIELD_IS_REQUIRED),
}

const usersSchema = {
  ownerFirstName: string().min(1, FIELD_IS_REQUIRED),
  ownerLastName: string().min(1, FIELD_IS_REQUIRED),
  ownerEmail: string().min(1, FIELD_IS_REQUIRED).email(EMAIL_IS_INVALID),
  regionalFirstName: string().optional(),
  regionalLastName: string().optional(),
  regionalEmail: string().email(EMAIL_IS_INVALID).optional().or(literal('')),
  facilityFirstName: string().optional(),
  facilityLastName: string().optional(),
  facilityEmail: string().email(EMAIL_IS_INVALID).optional().or(literal('')),
  supervisorFirstName: string().min(1, FIELD_IS_REQUIRED),
  supervisorLastName: string().min(1, FIELD_IS_REQUIRED),
  supervisorEmail: string().min(1, FIELD_IS_REQUIRED).email(EMAIL_IS_INVALID),
  supervisorId: string().optional(),
  ownerId: string().optional(),
  regionalManagerId: string().optional(),
  facilityManagerId: string().optional(),
}

const institutionalSchema = {
  organizationName: string().min(1, FIELD_IS_REQUIRED),
  organizationType: string().min(1, FIELD_IS_REQUIRED),
  organizationId: string().optional(),
  ownerId: string().optional(),
  regionalManagerId: string().optional(),
  facilityManagerId: string().optional(),
  supervisorId: string().optional(),
  twoDigitsOrg: string()
    .min(1, FIELD_IS_REQUIRED)
    .max(2, organizationValidationText)
    .refine((val) => /\d\d/.test(val), organizationValidationText),
  state: string().min(1, FIELD_IS_REQUIRED),
  specificUnitName: string().min(1, FIELD_IS_REQUIRED),
  twoDigitsSpecific: string()
    .min(1, FIELD_IS_REQUIRED)
    .max(2, specificUnitIDValidationText)
    .refine((val) => /[0-9a-z]{2}/i.test(val), specificUnitIDValidationText),
  duration: string().min(1, FIELD_IS_REQUIRED),
  numberOfUsers: coerce
    .number({
      required_error: FIELD_IS_REQUIRED,
      invalid_type_error: FIELD_IS_REQUIRED,
    })
    .min(1, FIELD_IS_REQUIRED),
}

const sharingSchema = {
  careTeamContentSharingDuration: string().nullable(),
  careTeamContentSharingEffectiveDate: any(),
  careTeamContentSharingExpiryDate: any(),
  careTeamContentSharingEnabled: boolean(),
}

const formSchema = (codeType: EAccessCodeType, isEdit: boolean, isDraft: boolean) => {
  let schema = {
    ...commonSchema,
    ...({} as typeof institutionalSchema),
    ...({} as typeof usersSchema),
    ...({} as typeof sharingSchema),
  }

  if (codeType === EAccessCodeType.institutionAccessCode) {
    schema = {
      ...schema,
      ...institutionalSchema,
      careTeamContentSharingEnabled: boolean(),
    }

    if (!isEdit || isDraft) {
      schema = {
        ...schema,
        ...usersSchema,
      }
    }

    if (isEdit && !isDraft) {
      schema = {
        ...schema,
        careTeamContentSharingDuration: string().nullable(),
        careTeamContentSharingEffectiveDate: any(),
        careTeamContentSharingExpiryDate: any(),
      }
    }
  }

  return object(schema)
    .refine(isExpiryLaterThatEffective, {
      path: ['expiryDate'],
      message: 'Expiry date must be later that Effective date',
    })
    .refine(
      (data) => {
        if (data.type === EAccessCodeType.promoCode) {
          return true
        }

        const formatRegex = new RegExp(/^\s*(\d+y\s+)?(\d+m\s+)?(\d+d\s*)?$/)
        return formatRegex.test(data.duration)
      },
      {
        path: ['duration'],
        message: 'Wrong duration format',
      },
    )
    .superRefine((data, ctx) => {
      const checkGroup = (prefix: 'regional' | 'facility') => {
        const firstName = data[`${prefix}FirstName`]?.trim()
        const lastName = data[`${prefix}LastName`]?.trim()
        const email = data[`${prefix}Email`]?.trim()

        const hasAny = firstName || lastName || email

        if (hasAny) {
          if (!firstName) {
            ctx.addIssue({
              code: ZodIssueCode.custom,
              message: FIELD_IS_REQUIRED,
              path: [`${prefix}FirstName`],
            })
          }

          if (!lastName) {
            ctx.addIssue({
              code: ZodIssueCode.custom,
              message: FIELD_IS_REQUIRED,
              path: [`${prefix}LastName`],
            })
          }

          if (!email) {
            ctx.addIssue({
              code: ZodIssueCode.custom,
              message: FIELD_IS_REQUIRED,
              path: [`${prefix}Email`],
            })
          }
        }
      }

      checkGroup('regional')
      checkGroup('facility')
    })
    .superRefine((data, ctx) => {
      if (data.type === EAccessCodeType.institutionAccessCode && isEdit) {
        if (data.careTeamContentSharingEnabled) {
          if (!data.careTeamContentSharingDuration) {
            ctx.addIssue({
              path: ['careTeamContentSharingDuration'],
              code: ZodIssueCode.custom,
              message: FIELD_IS_REQUIRED,
            })
          }
          if (!data.careTeamContentSharingEffectiveDate) {
            ctx.addIssue({
              path: ['careTeamContentSharingEffectiveDate'],
              code: ZodIssueCode.custom,
              message: FIELD_IS_REQUIRED,
            })
          }
          if (!data.careTeamContentSharingExpiryDate) {
            ctx.addIssue({
              path: ['careTeamContentSharingExpiryDate'],
              code: ZodIssueCode.custom,
              message: FIELD_IS_REQUIRED,
            })
          }
        }
      }
    })
    .refine(
      (data) => {
        if (data.type === EAccessCodeType.promoCode || !isEdit) {
          return true
        }

        if (!data.careTeamContentSharingDuration) return true

        const formatRegex = new RegExp(/^\s*(\d+y\s+)?(\d+m\s+)?(\d+d\s*)?$/)
        return formatRegex.test(data.careTeamContentSharingDuration)
      },
      {
        path: ['careTeamContentSharingDuration'],
        message: 'Wrong duration format',
      },
    )
}

export const formSettings = (
  codeType: EAccessCodeType,
  editedItem: Nullish<AccessCode>,
  isDraft: boolean = false,
) => ({
  mode: 'onChange' as const,
  resolver: isDraft
    ? zodResolver(accessCodeDraftSchema)
    : zodResolver(
        formSchema(codeType, !!editedItem, editedItem?.status === EAccessCodeStatus.draft),
      ),
  defaultValues: !editedItem
    ? {
        type: codeType,
        reason: '',
        organizationName: '',
        twoDigitsOrg: '',
        effectiveDate: null,
        expiryDate: null,
        organizationType: '',
        state: '',
        specificUnitName: '',
        twoDigitsSpecific: '',
        duration: '',
        numberOfUsers: undefined,
        organizationId: '',
        ownerId: '',
        regionalManagerId: '',
        facilityManagerId: '',
        careTeamContentSharingEnabled: true,
      }
    : parsedDefaultValues(editedItem, codeType, isDraft),
})

const parsedDefaultValues = (
  editedItem: AccessCode,
  codeType: EAccessCodeType,
  isDraft: boolean,
) => {
  if (isDraft) {
    return {
      ...editedItem,
      code: editedItem.code || '',
      twoDigitsSpecific: editedItem.specificTwoDigits,
      state: editedItem.specificState,
      type: codeType,
      organizationId: editedItem.organizationId?.toString() || '',
      organizationName: editedItem.organization?.name || editedItem.organizationName || '',
      twoDigitsOrg: editedItem.organization?.twoDigits || editedItem.twoDigitsOrg || '',
      organizationType: editedItem.organization?.type || editedItem.organizationType || '',
      supervisorId: editedItem.supervisorId?.toString() || '',
      duration: editedItem.duration || '',
      specificUnitName: editedItem.specificUnitName || '',
      specificTwoDigits: editedItem.specificTwoDigits || '',
      effectiveDate: editedItem.effectiveDate || '',
      expiryDate: editedItem.expiryDate || '',
      numberOfUsers: editedItem.numberOfUsers || 1,
      reason: editedItem.reason || '',
      ownerFirstName: editedItem.draftOwner?.firstName,
      ownerLastName: editedItem.draftOwner?.lastName,
      ownerEmail: editedItem.draftOwner?.email,
      regionalFirstName: editedItem.draftRegionalManager?.firstName,
      regionalLastName: editedItem.draftRegionalManager?.lastName,
      regionalEmail: editedItem.draftRegionalManager?.email,
      facilityFirstName: editedItem.draftFacilityManager?.firstName,
      facilityLastName: editedItem.draftFacilityManager?.lastName,
      facilityEmail: editedItem.draftFacilityManager?.email,
      supervisorFirstName: editedItem.draftSupervisor?.firstName,
      supervisorLastName: editedItem.draftSupervisor?.lastName,
      supervisorEmail: editedItem.draftSupervisor?.email,
      status: editedItem.status,
      organizationSingleLetter: editedItem.organization?.singleLetter,
      organizationTwoDigits: editedItem.organization?.twoDigits,
      ownerId: editedItem.ownerId?.toString() || '',
      regionalManagerId: editedItem.regionalManagerId?.toString() || '',
      facilityManagerId: editedItem.facilityManagerId?.toString() || '',
    }
  }
  return {
    ...editedItem,
    code: editedItem.code || '',
    twoDigitsSpecific: editedItem.specificTwoDigits,
    state: editedItem.specificState,
    type: codeType,
    organizationId: editedItem.organizationId?.toString() || '',
    organizationName: editedItem.organization?.name || editedItem.organizationName || '',
    twoDigitsOrg: editedItem.organization?.twoDigits || editedItem.twoDigitsOrg || '',
    organizationType: editedItem.organization?.type || editedItem.organizationType || '',
    supervisorId: editedItem.supervisorId?.toString() || '',
    ownerId: editedItem.ownerId?.toString() || '',
    regionalManagerId: editedItem.regionalManagerId?.toString() || '',
    facilityManagerId: editedItem.facilityManagerId?.toString() || '',
    careTeamContentSharingDuration: editedItem.careTeamContentSharingDuration || '',
    careTeamContentSharingEffectiveDate: editedItem.careTeamContentSharingEffectiveDate || '',
    careTeamContentSharingExpiryDate: editedItem.careTeamContentSharingExpiryDate || '',
    careTeamContentSharingEnabled: editedItem.careTeamContentSharingEnabled || false,
  }
}

export type FormType = TypeOf<ReturnType<typeof formSchema>>
